<template>
  <div :class="classes">
    <div class="banner__inner">
      <vue-client-only>
        <a
          class="banner__link"
          :href="getBannerData(data, ['body', 'url'])"
          :target="getBannerData(data, ['body', 'target'])"
          @click="onClick()"
        >
          <img
            class="banner__backgound-img"
            v-if="backAsImg"
            :src="getBannerData(data, ['body','image'])"
            :alt="getBannerData(data, ['body','alt'])"
          >
          <div
            v-else
            class="banner__background"
            :style="`background-image: url(${getBannerData(data, ['body','image'])})`"
          />
          <div
            v-if="getBannerData(data, ['body','svg'])"
            class="banner__top"
          >
            <div class="banner__foreground"
              :style="`background-image: url(${getBannerData(data, ['body','svg'])})`"
            />
          </div>
        </a>
      </vue-client-only>
    </div>
    <vue-client-only>
      <div
        :class="linksClasses(data)"
        v-if="getBannerData(data, ['links', 'length'])"
      >
        <a
          :href="link.url"
          :key="link.text"
          :style="fontStyles(link)"
          class="banner__links-item"
          v-for="link in getBannerData(data, ['links'])"
          @click="onClick(link)"
        >
          {{ link.text }}
        </a>
      </div>
    </vue-client-only>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';
import { throttle } from 'throttle-debounce';
import objectPath from 'object-path';
import { createGtmEventsProxy } from '@/gtm/gtm-events-utils';
import { bannersGtmEvents } from '@/gtm/banners-gtm-events';

export default {
  name: 'banner',

  components: {
    VueClientOnly,
  },

  inject: {
    adaptive: 'adaptive',
    gtmEvents: {
      default: createGtmEventsProxy(bannersGtmEvents),
    },
  },

  props: {
    data: {
      type: Object,
      required: true,
    },

    backAsImg: {
      type: Boolean,
      default: true,
    },

    slotName: {
      type: String,
      default: '',
    },

    // индекс баннера в свайпере
    index: {
      type: Number,
      default: 0,
    },

    // находится ли баннер в свайпере
    inSlider: {
      type: Boolean,
      default: false,
    },

    // активен ли сейчас баннер в свайпере
    isActive: {
      type: Boolean,
      default: false,
    }
  },

  computed: {
    classes() {
      const base = 'banner';

      return [base, {[`${base}_mode_slide`]: !this.backAsImg}];
    },
  },

  watch: {
    isActive: {
      immediate: true,
      handler(value, oldValue) {
        // если баннер находится в свайпере, и слайд активен, отправляем событие показа
        if (!this.$isServer && this.inSlider && value && !oldValue) {
          this.onShowBanner();
        }
      },
    },
  },

  created() {
    if(!this.$isServer) {
      window.addEventListener('resize', this.onResize);
      this.onResize();
    }
  },

  mounted() {
    // если баннер в свайпере, то отправляется событие в watch, тут не отправляем
    if (this.inSlider) return;
    this.onShowBanner();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },

  methods: {
    getBannerData(bannerData, path = []) {
      const key = this.adaptive.isMobile ? 'mobile' : 'desktop';

      return objectPath.get(bannerData.content?.[key], path);
    },
    onResize: throttle(100, function() {
      if(this.adaptive.isMobile) {
        this.slideButtonsPositionReset();
        this.slideButtonsPosition();
      } else {
        this.slideButtonsPositionReset();
      }
    }),
    slideButtonsPositionReset() {
      const linksWraps = document.querySelectorAll('.banner__links');

      linksWraps.forEach(linkWrap => {
        linkWrap.removeAttribute('style');
      });
    },
    // для мобильной версии, кнопки подтягиваем на середину
    slideButtonsPosition() {
      const linksWraps = document.querySelectorAll('.banner__links');

      linksWraps.forEach(linkWrap => {
        const buttonsCount = linkWrap.childElementCount;
        const rows = Math.ceil(buttonsCount / 2);

        if (rows >= 2) {
          // высота для отступов между кнопками
          linkWrap.style.minHeight = `${linkWrap.offsetHeight + (rows * 6)}px`;
        }

        const halfLinksWrap = linkWrap.offsetHeight / 2;

        linkWrap.style.marginTop = `-${halfLinksWrap}px`;
      });
    },
    linksClasses(bannerData) {
      return ['banner__links', this.getBannerData(bannerData, ['linksAlign']), {
        'single': this.getBannerData(bannerData, ['links']).length === 1,
      }];
    },
    fontStyles(linkData) {
      if (this.adaptive.isMobile) return;

      const color = linkData.font.color;

      return `color: ${color}; border-color: ${color};`;
    },
    onClick(link) {
      this.gtmEvents.clickBanner({
        slotName: this.slotName,
        imageUrl: this.getBannerData(this.data, ['body', 'image']),
        hostUrl: this.$route.fullPath,
        text: link?.text,
        redirectUrl: link?.url || this.getBannerData(this.data, ['body', 'url']),
        index: this.inSlider ? this.index : null,
      });
    },
    onShowBanner() {
      this.gtmEvents.showBanner({
        slotName: this.slotName,
        imageUrl: this.getBannerData(this.data, ['body', 'image']),
        hostUrl: this.$route.fullPath,
        redirectUrl: this.getBannerData(this.data, ['body', 'url']),
        index: this.inSlider ? this.index : null,
      });
    },
  },
}
</script>

<style lang="scss">
  .banner {
    position: relative;

    &_mode_slide {
      max-width: 1920px;
      margin: 0 auto;

      .banner__inner {
        height: 272px;

        @include breakpoint("tablet") {
          height: 306px;
        }

        @include breakpoint("desktop") {
          height: 520px;
        }
      }
    }

    &:not(.banner_mode_slide) {
      .banner__links {
        @include breakpoint("tablet") {
          left: 16px;
          right: 16px;
        }

        @include breakpoint("desktop") {
          left: 24px;
          right: 24px;
        }
      }
    }

    &__link {
      display: block;
      height: 100%;
      font-size: 0;
      text-align: center;
    }

    &__backgound-img {
      max-width: 100%;
      height: auto;
    }

    &__background,
    &__foreground {
      height: 100%;
      background-size: cover;
      background-repeat: no-repeat;
      background-position: top center;
    }

    &__foreground {
      max-width: 1224px;
      margin: auto;
    }

    &__top {
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      width: 100%;
    }

    &__links {
      background: $white;
      padding: 24px 16px;
      border: 1px solid #f0f0f0;
      box-shadow: 0 4px 24px rgba(0, 0, 0, .08);
      margin-left: 16px;
      margin-right: 16px;
      display: flex;
      flex-wrap: wrap;
      align-content: space-between;
      z-index: 1;
      position: relative;
      justify-content: space-between;

      &.single {
        justify-content: center;
      }

      @include breakpoint("tablet") {
        background: none;
        padding: 0;
        height: auto;
        box-shadow: none;
        justify-content: unset;
        border: 0;
        bottom: 64px;
        max-width: 615px;
        margin: auto;
        position: absolute;
        left: 0;
        right: 0;

        &.left {
          justify-content: flex-start;
        }

        &.right {
          justify-content: flex-end;
        }

        &.center {
          justify-content: center;
        }
      }

      @include breakpoint("desktop") {
        max-width: 1036px;
        bottom: 110px;
      }
    }

    &__links-item {
      background: $blue;
      font-size: 14px;
      font-weight: 600;
      color: $white;
      border-radius: 4px;
      text-align: center;
      line-height: 32px;
      height: 32px;
      flex: 0 0 calc(50% - 6px);

      @include breakpoint("tablet") {
        font-size: 16px;
        display: block;
        color: $white;
        background: none;
        border: 0;
        border-bottom: 2px solid $white;
        flex: 0 0 auto;
        border-radius: 0;
        line-height: 20px;
        height: 30px;
        margin-right: 16px;

        &:last-child {
          margin-right: 0;
        }
      }

      @include breakpoint("desktop") {
        margin-right: 24px;
      }
    }
  }
</style>
